import { makeObservable, observable, runInAction } from 'mobx';
import { initialMatrix } from '../components/TimetableVersion/utils/timetableVersionTableUtils';
import { VERSION_RESOURCE_TYPE } from '../components/TimetableVersion/utils/versionResourceType';
import { getCacheTag } from '../hooks/useMemorizedCacheTag';
import type { WeekAvailability } from '../types/availability';
import {
  SetTimetableVersionAvailabilityDocument,
  SetTimetableVersionAvailabilityMutation,
  SetTimetableVersionAvailabilityMutationVariables,
} from '../types/planung-graphql-client-defs';
import { urqlClient } from '../utils/urqlClient';

export type Availabilities = {
  class: {
    uuid: string;
    availabilities: WeekAvailability;
  }[];
  teacher: {
    uuid: string;
    availabilities: WeekAvailability;
  }[];
  room: {
    uuid: string;
    availabilities: WeekAvailability;
  }[];
  subject: {
    uuid: string;
    availabilities: WeekAvailability;
  }[];
};

export class AvailabilityStore {
  public availabilities: Availabilities;

  constructor() {
    this.availabilities = {
      class: [],
      teacher: [],
      room: [],
      subject: [],
    };
    makeObservable(this, {
      availabilities: observable,
    });
  }

  public async setAvailability(
    type: keyof Availabilities,
    uuid: string,
    availability: WeekAvailability,
    versionUuid?: string,
  ) {
    runInAction(() => {
      const currentTypeAvailabilities = this.availabilities[type];
      if (currentTypeAvailabilities.length === 0) {
        this.availabilities[type] = [
          {
            uuid,
            availabilities: availability,
          },
        ];
        return;
      } else {
        const currentAvailability = currentTypeAvailabilities.find((a) => a.uuid === uuid);
        if (currentAvailability) {
          currentAvailability.availabilities = availability;
        } else {
          this.availabilities[type].push({
            uuid,
            availabilities: availability,
          });
        }
      }
    });
    if (versionUuid) {
      await this.saveAvailability(type, uuid, availability, versionUuid);
    }
  }

  public getAvailability(type: keyof Availabilities, uuid: string): WeekAvailability {
    return this.availabilities[type].find((entry) => entry.uuid === uuid)?.availabilities ?? initialMatrix;
  }

  private async saveAvailability(
    type: keyof Availabilities,
    resourceUuid: string,
    availability: WeekAvailability,
    versionUuid: string,
  ) {
    const versionsContext = getCacheTag('TIMETABLE_VERSIONS');
    await urqlClient
      .mutation<SetTimetableVersionAvailabilityMutation, SetTimetableVersionAvailabilityMutationVariables>(
        SetTimetableVersionAvailabilityDocument,
        {
          versionUuid,
          resourceType: VERSION_RESOURCE_TYPE[type],
          resourceUuid,
          availability: {
            mon: availability.mon,
            tue: availability.tue,
            wed: availability.wed,
            thu: availability.thu,
            fri: availability.fri,
            sat: availability.sat,
            sun: availability.sun,
          },
        },
        versionsContext,
      )
      .toPromise();
  }
}
